import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiPlay, FiVolume2, FiMaximize, FiShield, FiStar, FiArrowDown } from 'react-icons/fi';

const VSLHero = () => {
  const [playing, setPlaying] = useState(false);

  return (
    <section className="relative min-h-[90vh] flex flex-col justify-center overflow-hidden bg-[#faf8f5]">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,rgba(217,119,6,0.08),transparent)]" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-amber-50 rounded-full blur-[120px] opacity-60 translate-x-1/3 -translate-y-1/3" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-stone-100 rounded-full blur-[100px] opacity-80 -translate-x-1/3 translate-y-1/3" />
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=60 height=60 viewBox=0 0 60 60 xmlns=http://www.w3.org/2000/svg%3E%3Cg fill=none fill-rule=evenodd%3E%3Cg fill=%23d97706 fill-opacity=0.03%3E%3Cpath d=M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-40" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10 pt-16 pb-0">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-10"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-1.5">
              {[...Array(5)].map((_, i) => (
                <SafeIcon key={i} icon={FiStar} className="w-4 h-4 text-amber-500 fill-current" />
              ))}
            </div>
            <span className="text-stone-500 text-base font-medium">Physician-supervised weight loss</span>
          </div>

          <span className="inline-block px-5 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-sm font-bold uppercase tracking-widest mb-7">
            A 6-Week Medical Weight Loss Challenge
          </span>

          <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-stone-900 font-serif leading-[1.05] tracking-tight mb-7">
            Where Could You Be<br />
            <span className="shimmer-text">6 Weeks From Today?</span>
          </h1>

          <p className="text-xl sm:text-2xl text-stone-600 leading-relaxed mb-4 font-light max-w-2xl mx-auto">
            Watch the short video below to see exactly how our physician-led program works and why it succeeds where everything else has failed.
          </p>

          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
            className="flex items-center justify-center gap-2 text-amber-600 font-bold text-sm uppercase tracking-widest mb-8"
          >
            <SafeIcon icon={FiArrowDown} className="text-base" />
            Watch Now
            <SafeIcon icon={FiArrowDown} className="text-base" />
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="relative w-full rounded-t-[2rem] overflow-hidden shadow-2xl shadow-stone-900/20 border border-stone-200/60"
          style={{ aspectRatio: '16/9' }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-stone-700 to-stone-900">
            <img
              src="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?q=80&w=1200&auto=format&fit=crop"
              alt="Program Specialist Stuart"
              className="w-full h-full object-cover opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-stone-900/20 to-transparent" />
          </div>

          {!playing && (
            <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
              <motion.button
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setPlaying(true)}
                className="w-20 h-20 sm:w-24 sm:h-24 bg-white rounded-full flex items-center justify-center shadow-2xl mb-5"
              >
                <SafeIcon icon={FiPlay} className="text-stone-900 text-3xl sm:text-4xl ml-1" />
              </motion.button>
              <div className="text-center">
                <p className="text-white font-bold text-base sm:text-lg font-serif">Stuart — Program Specialist</p>
                <p className="text-stone-300 text-sm sm:text-base mt-1">5-Minute Overview — Watch Before Enrolling</p>
              </div>
            </div>
          )}

          <div className="absolute bottom-0 left-0 right-0 bg-stone-900/80 px-4 py-3 z-10">
            <div className="w-full h-1 bg-stone-700 rounded-full mb-2">
              <div className="h-full w-0 bg-amber-400 rounded-full" />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiPlay} className="text-white text-sm" />
                <SafeIcon icon={FiVolume2} className="text-white text-sm" />
                <span className="text-stone-400 text-xs">0:00 / 5:00</span>
              </div>
              <SafeIcon icon={FiMaximize} className="text-white text-sm" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="flex flex-wrap items-center justify-center gap-8 py-6 border-b border-stone-200/60 text-sm font-bold uppercase tracking-widest text-stone-500"
        >
          <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-400" />Medical Supervision</span>
          <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-400" />FDA-Approved Rx</span>
          <span className="flex items-center gap-2"><SafeIcon icon={FiShield} className="text-amber-500 text-base" />6-Week Results Guarantee</span>
        </motion.div>
      </div>
    </section>
  );
};

export default VSLHero;